import { useCallback, useEffect, useState } from 'react'
import { ApiService, FilterSchema } from '../services/api'
import { Product } from '../types/product'
import { useErrorHandler } from './useErrorHandler'
import { getMinEffectivePrice } from '../utils/cartUtils'
import {
  CAPSULE_ALIASES,
  categorySlice,
  COFFEE_ALIASES,
  DRIP_ALIASES,
  sortByDisplayOrder,
} from '../utils/productCategories'

interface UseProductsParams {
  category?: string
  productSlug?: string
  beanType?: string
  processingMethod?: string
  originCountry?: string
  minPrice?: number
  maxPrice?: number
  minScaScore?: number
  maxScaScore?: number
  tags?: string[]
  tagFilter?: string
}

export const useProducts = (params: UseProductsParams = {}) => {
  const {
    category,
    productSlug,
    beanType,
    processingMethod,
    originCountry,
    minPrice,
    maxPrice,
    minScaScore,
    maxScaScore,
    tags,
    tagFilter,
  } = params

  const [products, setProducts] = useState<Product[]>([])
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([])
  const [product, setProduct] = useState<Product | null>(null)
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([])
  const [newProducts, setNewProducts] = useState<Product[]>([])
  const [coffeeProducts, setCoffeeProducts] = useState<Product[]>([])
  const [dripProducts, setDripProducts] = useState<Product[]>([])
  const [capsuleProducts, setCapsuleProducts] = useState<Product[]>([])
  const [filterSchema, setFilterSchema] = useState<FilterSchema | undefined>(undefined)

  // Окремі стани завантаження для кожної категорії
  const [loadingNew, setLoadingNew] = useState(true)
  const [loadingCoffee, setLoadingCoffee] = useState(true)
  const [loadingDrip, setLoadingDrip] = useState(true)
  const [loadingCapsule, setLoadingCapsule] = useState(true)
  const [loading, setLoading] = useState(true)
  const [loadingProduct, setLoadingProduct] = useState(false)
  const [loadingRelated, setLoadingRelated] = useState(false)

  const [error, setError] = useState<string | null>(null)
  const throwError = useErrorHandler()

  // Функція для фільтрації продуктів на основі параметрів
  const filterProducts = useCallback(
    (allProducts: Product[]) => {
      return allProducts.filter(product => {
        // Фільтр по категорії
        if (category && product.productCategory !== category) {
          return false
        }

        // Фільтр по типу бобів
        if (beanType && product.beanType?.value !== beanType) {
          return false
        }

        // Фільтр по методу обробки
        if (processingMethod && product.processingMethod?.value !== processingMethod) {
          return false
        }

        // Фільтр по країні походження
        if (originCountry && product.originCountry !== originCountry) {
          return false
        }

        // Фільтр по ціні
        const productMinPrice = getMinEffectivePrice(product.prices || [])
        if (minPrice !== undefined && (productMinPrice === null || productMinPrice < minPrice)) {
          return false
        }
        if (maxPrice !== undefined && (productMinPrice === null || productMinPrice > maxPrice)) {
          return false
        }

        // Фільтр по SCAA оцінці
        if (minScaScore !== undefined && product.scaScore < minScaScore) {
          return false
        }
        if (maxScaScore !== undefined && product.scaScore > maxScaScore) {
          return false
        }

        // Фільтр по тегам
        if (tags && tags.length > 0) {
          const productTags = product.tags?.map(tag => tag.name.toLowerCase()) || []
          const hasMatchingTag = tags.some(tag => productTags.includes(tag.toLowerCase()))
          if (!hasMatchingTag) {
            return false
          }
        }

        return true
      })
    },
    [
      category,
      beanType,
      processingMethod,
      originCountry,
      minPrice,
      maxPrice,
      minScaScore,
      maxScaScore,
      tags,
    ],
  )

  // Функція для отримання релевантних товарів
  const fetchRelatedProducts = async (currentProduct: Product) => {
    try {
      setLoadingRelated(true)

      const allProducts = await ApiService.getProducts({
        category: currentProduct.categoryInternalName,
        limit: 20,
      })

      // Ensure allProducts is an array
      if (!Array.isArray(allProducts)) {
        setRelatedProducts([])
        return
      }

      // Validate product structure
      const validProducts = allProducts.filter(
        product =>
          product &&
          typeof product === 'object' &&
          product.id &&
          product.name &&
          Array.isArray(product.tags),
      )

      // Filtered out invalid products

      // Use validProducts for filtering
      const productsToFilter = validProducts

      // Фільтруємо релевантні товари
      const related = productsToFilter
        .filter(p => p && p.id && p.id !== currentProduct.id) // Виключаємо поточний товар
        .filter(p => {
          // Ensure product has required properties
          if (!p.productCategory || !p.originCountry || !p.beanType || !p.processingMethod) {
            return false
          }

          // Пріоритет 1: та ж категорія
          if (p.productCategory === currentProduct.productCategory) {
            return true
          }

          // Пріоритет 2: та ж країна походження
          if (p.originCountry === currentProduct.originCountry) {
            return true
          }

          // Пріоритет 3: схожий тип зерна
          if (
            p.beanType &&
            p.beanType.value &&
            currentProduct.beanType &&
            currentProduct.beanType.value
          ) {
            if (p.beanType.value === currentProduct.beanType.value) {
              return true
            }
          }

          // Пріоритет 4: схожий метод обробки
          if (
            p.processingMethod &&
            p.processingMethod.value &&
            currentProduct.processingMethod &&
            currentProduct.processingMethod.value
          ) {
            if (p.processingMethod.value === currentProduct.processingMethod.value) {
              return true
            }
          }

          // Пріоритет 5: схожі смакові характеристики
          if (
            typeof p.sweetness === 'number' &&
            typeof p.acidity === 'number' &&
            typeof p.bitterness === 'number' &&
            typeof currentProduct.sweetness === 'number' &&
            typeof currentProduct.acidity === 'number' &&
            typeof currentProduct.bitterness === 'number'
          ) {
            const currentSweetness = currentProduct.sweetness
            const currentAcidity = currentProduct.acidity
            const currentBitterness = currentProduct.bitterness

            const productSweetness = p.sweetness
            const productAcidity = p.acidity
            const productBitterness = p.bitterness

            // Перевіряємо схожість смакових характеристик (в межах ±2 балів)
            const sweetnessDiff = Math.abs(currentSweetness - productSweetness)
            const acidityDiff = Math.abs(currentAcidity - productAcidity)
            const bitternessDiff = Math.abs(currentBitterness - productBitterness)

            if (sweetnessDiff <= 2 && acidityDiff <= 2 && bitternessDiff <= 2) {
              return true
            }
          }

          return false
        })
        .slice(0, 6) // Обмежуємо до 6 товарів

      setRelatedProducts(related)
    } catch (err) {
      setRelatedProducts([])
    } finally {
      setLoadingRelated(false)
    }
  }

  // Завантаження окремого продукту за ID
  useEffect(() => {
    if (productSlug) {
      const fetchProduct = async () => {
        try {
          setLoadingProduct(true)
          setError(null)

          const foundProduct = await ApiService.getProductBySlug(productSlug)

          if (foundProduct) {
            setProduct(foundProduct)
            // Завантажуємо релевантні товари після отримання основного товару
            await fetchRelatedProducts(foundProduct)
          } else {
            setError('Товар не знайдено')
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Помилка завантаження товару'
          setError(errorMessage)
          setProduct(null)
          if (errorMessage.includes('Network error')) {
            throwError(err)
          }
        } finally {
          setLoadingProduct(false)
        }
      }

      fetchProduct()
    }
  }, [productSlug, throwError])

  // Завантаження продуктів (фільтрація на фронті; tagFilter — серверна)
  useEffect(() => {
    if (!productSlug) {
      const fetchProducts = async () => {
        try {
          setLoading(true)
          setError(null)

          let allProducts: Product[]

          const queryParams: any = { limit: 100 }

          if (tagFilter) {
            queryParams.tags = [tagFilter]
          }

          try {
            const { products: fetchedProducts, schema } = await ApiService.getProductsAndSchema(
              queryParams,
            )
            allProducts = fetchedProducts
            setFilterSchema(schema)
          } catch (e) {
            // Fallback до продуктів без схеми
            allProducts = await ApiService.getProducts(queryParams)
          }

          if (!Array.isArray(allProducts)) {
            allProducts = []
          } else {
            const validProducts = allProducts.filter(
              product =>
                product &&
                typeof product === 'object' &&
                product.id &&
                product.name &&
                Array.isArray(product.tags),
            )

            allProducts = validProducts
          }

          const sortedProducts = sortByDisplayOrder(allProducts)

          setProducts(sortedProducts)

          const filtered = filterProducts(sortedProducts)
          setFilteredProducts(filtered)

          const newProducts = sortedProducts
            .filter(product => product && product.tags && Array.isArray(product.tags))
            .filter(product => product.tags.some(tag => tag && tag.name === 'новинка'))
            .slice(0, 10)

          setNewProducts(newProducts)
          setCoffeeProducts(categorySlice(sortedProducts, COFFEE_ALIASES))
          setDripProducts(categorySlice(sortedProducts, DRIP_ALIASES))
          setCapsuleProducts(categorySlice(sortedProducts, CAPSULE_ALIASES))
          setLoadingNew(false)
          setLoadingCoffee(false)
          setLoadingDrip(false)
          setLoadingCapsule(false)
          setLoading(false)
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Помилка завантаження товарів'
          setError(errorMessage)
          setLoadingNew(false)
          setLoading(false)

          setProducts([])
          setFilteredProducts([])
          setNewProducts([])
          setCoffeeProducts([])
          setDripProducts([])
          setCapsuleProducts([])
          setLoadingCoffee(false)
          setLoadingDrip(false)
          setLoadingCapsule(false)

          // Network errors → bubble to ErrorBoundary
          if (errorMessage.includes('Network error')) {
            throwError(err)
          }
        }
      }

      fetchProducts()
    } else {
      setLoadingCoffee(false)
      setLoadingDrip(false)
      setLoadingCapsule(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSlug, tagFilter]) // Видалено filterProducts з залежностей, щоб уникнути нескінченного циклу

  // Оновлюємо відфільтровані продукти при зміні параметрів фільтрації
  useEffect(() => {
    if (products.length > 0) {
      const filtered = filterProducts(products)
      setFilteredProducts(filtered)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    category,
    beanType,
    processingMethod,
    originCountry,
    minPrice,
    maxPrice,
    minScaScore,
    maxScaScore,
    tags,
    products,
    // Видалено filterProducts з залежностей, щоб уникнути нескінченного циклу
  ])

  return {
    products,
    filteredProducts,
    product,
    relatedProducts,
    newProducts,
    coffeeProducts,
    dripProducts,
    capsuleProducts,
    loading,
    loadingProduct,
    loadingRelated,
    loadingNew,
    loadingCoffee,
    loadingDrip,
    loadingCapsule,
    error,
    filterSchema,
  }
}
